import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Products.css';

const products = [
  {
    id: 'panels',
    name: 'Helios Panels',
    tag: 'Photovoltaic Modules',
    headline: 'TOPCon bifacial modules with 22.8% efficiency.',
    body: 'Half-cell, multi-busbar mono PERC and TOPCon modules from 400W residential to 710W utility-scale. Anti-PID, low LID, and exceptional low-light performance.',
    specs: [
      { k: 'Power', v: '400 – 710 Wp' },
      { k: 'Efficiency', v: 'up to 22.8%' },
      { k: 'Warranty', v: '25 years' },
      { k: 'Temp Coef.', v: '-0.29%/°C' },
    ],
    visual: 'panel',
  },
  {
    id: 'inverters',
    name: 'Pulse Inverters',
    tag: 'String & Hybrid Inverters',
    headline: 'From 3 kW home hybrids to 350 kW utility strings.',
    body: 'IP66-rated, transformer-less inverters with MLPE support, 99% peak efficiency, and dual-MPPT optimization. Hybrid variants integrate seamlessly with Helios Storage.',
    specs: [
      { k: 'Range', v: '3 – 350 kW' },
      { k: 'Efficiency', v: '99% CEC' },
      { k: 'MPPT', v: '2 – 12 ch.' },
      { k: 'Rating', v: 'IP66' },
    ],
    visual: 'inverter',
  },
  {
    id: 'storage',
    name: 'Helios Storage',
    tag: 'LiFePO₄ Battery Systems',
    headline: 'Safe, stackable lithium storage from 5 kWh to 1 MWh.',
    body: 'LFP chemistry, liquid-cooled modules, 6000-cycle life. Modular rack architecture scales from residential wall units to containerized grid-scale installations.',
    specs: [
      { k: 'Capacity', v: '5 kWh – 1 MWh' },
      { k: 'Cycles', v: '6,000 @ 80% DoD' },
      { k: 'Round-trip', v: '96%' },
      { k: 'Chemistry', v: 'LiFePO₄' },
    ],
    visual: 'battery',
  },
  {
    id: 'ev',
    name: 'Sol Charge',
    tag: 'Solar EV Infrastructure',
    headline: 'Solar-fed DC fast charging, up to 240 kW per port.',
    body: 'Integrated solar canopy + bidirectional DC fast charger. OCPP 2.0.1 compliant, V2G-ready, with native Helios monitoring for fleet-scale deployments.',
    specs: [
      { k: 'Power', v: '7 – 240 kW' },
      { k: 'Ports', v: 'CCS2 / CHAdeMO' },
      { k: 'Protocol', v: 'OCPP 2.0.1' },
      { k: 'V2G', v: 'Ready' },
    ],
    visual: 'ev',
  },
];

function Visual({ type }) {
  if (type === 'panel') {
    return (
      <div className="viz viz-panel">
        <div className="panel-frame">
          {Array.from({ length: 6 }).map((_, r) => (
            <div className="panel-row" key={r}>
              {Array.from({ length: 10 }).map((_, c) => (
                <div className="panel-cell" key={c} style={{ animationDelay: `${(r * 10 + c) * 40}ms` }} />
              ))}
            </div>
          ))}
        </div>
        <div className="panel-reflect" />
      </div>
    );
  }
  if (type === 'inverter') {
    return (
      <div className="viz viz-inverter">
        <div className="inv-box">
          <div className="inv-screen">
            <div className="inv-bar" style={{ width: '78%' }} />
            <div className="inv-bar" style={{ width: '45%' }} />
            <div className="inv-bar" style={{ width: '92%' }} />
          </div>
          <div className="inv-led l1" />
          <div className="inv-led l2" />
          <div className="inv-led l3" />
        </div>
      </div>
    );
  }
  if (type === 'battery') {
    return (
      <div className="viz viz-battery">
        {Array.from({ length: 4 }).map((_, i) => (
          <div className="bat-cell" key={i} style={{ animationDelay: `${i * 200}ms` }}>
            <div className="bat-fill" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="viz viz-ev">
      <div className="ev-car" />
      <div className="ev-plug" />
      <div className="ev-flow"><span /><span /><span /></div>
    </div>
  );
}

export default function Products() {
  const [active, setActive] = useState(products[0].id);
  const current = products.find((p) => p.id === active);

  return (
    <section id="products" className="products">
      <div className="container">
        <div className="products-head">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="eyebrow">What we build</span>
            <h2 className="section-title">
              A <span className="accent">complete solar ecosystem</span>,<br />
              engineered to work as one.
            </h2>
          </motion.div>
          <p className="section-lead">
            Every product in our lineup is designed for interoperability — from the silicon
            wafer to the cloud dashboard. One vendor, one standard, one warranty.
          </p>
        </div>

        <div className="products-tabs">
          {products.map((p) => (
            <button
              key={p.id}
              className={`p-tab ${active === p.id ? 'active' : ''}`}
              onClick={() => setActive(p.id)}
            >
              <span className="p-tab-name">{p.name}</span>
              <span className="p-tab-tag">{p.tag}</span>
              {active === p.id && (
                <motion.span className="p-tab-ind" layoutId="p-tab-ind" />
              )}
            </button>
          ))}
        </div>

        <div className="product-stage">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              className="product-card glass"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="product-content">
                <span className="eyebrow">{current.tag}</span>
                <h3 className="product-title">{current.headline}</h3>
                <p className="product-body">{current.body}</p>
                <div className="product-specs">
                  {current.specs.map((s) => (
                    <div key={s.k} className="spec">
                      <span className="spec-k">{s.k}</span>
                      <span className="spec-v">{s.v}</span>
                    </div>
                  ))}
                </div>
                <a href="#contact" className="btn btn-primary">Request Datasheet →</a>
              </div>
              <div className="product-visual">
                <Visual type={current.visual} />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
